/**
 * Errores de carga de bundles (`import()` de una página o modal lazy).
 *
 * Después de un deploy los bundles viejos desaparecen del servidor; una app
 * que quedó abierta (o que el celular restauró desde caché) pide un chunk con
 * el hash anterior y recibe 404. La única salida es recargar para traer el
 * `index.html` nuevo: `React.lazy` recuerda el rechazo y no reintenta.
 */
const CHUNK_ERROR_MESSAGE =
  /Loading (CSS )?chunk [\w-]+ failed|Failed to fetch dynamically imported module|Importing a module script failed|error loading dynamically imported module/i;

const RELOAD_AT_KEY = "gestor679_chunk_reload_at";
/** Si el bundle nuevo también falla, mejor mostrar el error que recargar en bucle. */
const RELOAD_LOOP_WINDOW_MS = 60_000;

export const isChunkLoadError = (error: unknown): boolean => {
  if (!error || typeof error !== "object") return false;
  const { name, code, message } = error as { name?: unknown; code?: unknown; message?: unknown };
  if (name === "ChunkLoadError" || code === "CHUNK_LOAD_FAILED" || code === "CSS_CHUNK_LOAD_FAILED") {
    return true;
  }
  return typeof message === "string" && CHUNK_ERROR_MESSAGE.test(message);
};

const readLastReload = (): number => {
  try {
    return Number(window.sessionStorage.getItem(RELOAD_AT_KEY)) || 0;
  } catch {
    return 0;
  }
};

const writeLastReload = (at: number) => {
  try {
    window.sessionStorage.setItem(RELOAD_AT_KEY, String(at));
  } catch {
    // Sin sessionStorage (modo privado) se recarga igual; el bucle lo corta el navegador.
  }
};

/** Recarga la app una sola vez por minuto. Devuelve false cuando ya se recargó hace poco. */
export const reloadOnceForChunkError = (now = Date.now()): boolean => {
  if (now - readLastReload() < RELOAD_LOOP_WINDOW_MS) return false;
  writeLastReload(now);
  window.location.reload();
  return true;
};
